import { useTsRest } from '@/_shared/context/ts-rest.js';
import { useToast } from '@/_shared/hooks/toast.js';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Spinner,
    Textarea,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle16Regular, Dismiss24Regular, Document24Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { calculateFileHash } from '@this/app-shared/dist/tools/hash.js';
import { trackException } from '@this/app-shared/dist/tools/insights/track.js';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

type UploadingFile = {
    hash: string;
    file: File;
    uploadProgress: number;
    status: 'uploading' | 'error';
    error: string | null;
    abortController: AbortController;
};

type UploadedFile = {
    hash: string;
    name: string;
    mimetype: string;
};

type SubmitDocumentsModalProps = {
    claimId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const useStyles = makeStyles({
    dropzone: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: tokens.spacingVerticalXXL,
        border: `2px dashed ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusLarge,
        cursor: 'pointer',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    fileList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        marginTop: tokens.spacingVerticalM,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusMedium,
    },
    fileIcon: {
        fontSize: '24px',
    },
    fileName: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    removeButton: {
        cursor: 'pointer',
    },
    dialogBackdrop: {
        backgroundColor: 'transparent',
    },
});

export function SubmitDocumentsModal({ claimId, open, onOpenChange }: SubmitDocumentsModalProps) {
    const styles = useStyles();
    const tsr = useTsRest();
    const toast = useToast();
    const queryClient = useQueryClient();

    const [message, setMessage] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: getFileUploadUrl } = tsr.claimDetails.getFileUploadUrl.useMutation();
    const { mutateAsync: submitDocuments } = tsr.claimDetails.submitAdditionalDocuments.useMutation();

    const uploadFile = useCallback(
        async (hash: string, file: File, abortController: AbortController) => {
            try {
                // Step 1: Get upload URL
                const urlResponse = await getFileUploadUrl({
                    params: { claimId },
                    body: {
                        fileHash: hash,
                    },
                });

                if (urlResponse.status !== 200) {
                    throw new Error('Failed to get upload URL');
                }

                const { uploadUrl } = urlResponse.body;

                // Step 2: Upload to blob storage
                await new Promise<void>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();

                    abortController.signal.addEventListener('abort', () => {
                        xhr.abort();
                    });

                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            const percentComplete = Math.round((e.loaded / e.total) * 100);
                            setUploadingFiles((prev) =>
                                prev.map((f) => (f.hash === hash ? { ...f, uploadProgress: percentComplete } : f))
                            );
                        }
                    });

                    xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            reject(new Error(`Upload failed with status ${xhr.status}`));
                        }
                    });

                    xhr.addEventListener('error', () => {
                        reject(new Error('Upload failed'));
                    });

                    xhr.addEventListener('abort', () => {
                        reject(new Error('Upload cancelled'));
                    });

                    xhr.open('PUT', uploadUrl);
                    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
                    xhr.send(file);
                });

                // Mark as successfully uploaded
                setUploadingFiles((prev) => prev.filter((f) => f.hash !== hash));
                setUploadedFiles((prev) => {
                    if (prev.some((f) => f.hash === hash)) {
                        return prev;
                    }

                    return [
                        ...prev,
                        {
                            hash,
                            name: file.name,
                            mimetype: file.type || 'application/octet-stream',
                        },
                    ];
                });
            } catch (error) {
                setUploadingFiles((prev) =>
                    prev.map((f) =>
                        f.hash === hash
                            ? {
                                  ...f,
                                  status: 'error' as const,
                                  error: error instanceof Error ? error.message : 'Unknown error',
                              }
                            : f
                    )
                );
                trackException(error);
            }
        },
        [claimId, getFileUploadUrl]
    );

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            for (const file of acceptedFiles) {
                try {
                    const hash = await calculateFileHash(file);

                    setUploadingFiles((prev) => {
                        if (prev.some((f) => f.hash === hash)) {
                            return prev;
                        }

                        const abortController = new AbortController();

                        uploadFile(hash, file, abortController);

                        return [
                            ...prev,
                            {
                                hash,
                                file,
                                uploadProgress: 0,
                                status: 'uploading',
                                error: null,
                                abortController,
                            },
                        ];
                    });
                } catch (error) {
                    toast('Fehler', `Datei "${file.name}" konnte nicht verarbeitet werden.`, {
                        intent: 'error',
                    });
                    trackException(error);
                }
            }
        },
        [uploadFile, toast]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
    });

    const handleRemoveFile = useCallback((hash: string) => {
        setUploadedFiles((prev) => prev.filter((f) => f.hash !== hash));
    }, []);

    const handleCancelUpload = useCallback((hash: string) => {
        setUploadingFiles((prev) => {
            const files = prev.filter((f) => f.hash === hash);
            files.forEach((f) => f.abortController.abort());
            return prev.filter((f) => f.hash !== hash);
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!message.trim()) {
            toast('Fehler', 'Bitte geben Sie eine Nachricht ein.', { intent: 'error' });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await submitDocuments({
                params: { claimId },
                body: {
                    message,
                    files: uploadedFiles,
                },
            });

            if (response.status === 200 && response.body.success) {
                toast('Erfolg', 'Unterlagen wurden erfolgreich nachgereicht.', { intent: 'success' });

                // Invalidate communication query to show the new email
                await queryClient.invalidateQueries({
                    queryKey: ['claimDetails', 'getCommunicationItems', claimId],
                });

                // Reset form
                setMessage('');
                setUploadedFiles([]);
                setUploadingFiles([]);

                // Close modal
                onOpenChange(false);
            } else {
                throw new Error('Submission failed');
            }
        } catch (error) {
            toast('Fehler', 'Unterlagen konnten nicht nachgereicht werden.', { intent: 'error' });
            trackException(error);
        } finally {
            setIsSubmitting(false);
        }
    }, [message, uploadedFiles, claimId, submitDocuments, toast, onOpenChange, queryClient]);

    return (
        <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
            <DialogSurface style={{ maxWidth: '600px' }} backdrop={{ className: styles.dialogBackdrop }}>
                <DialogBody>
                    <DialogTitle>Unterlagen nachreichen</DialogTitle>
                    <DialogContent>
                        <Field label="Nachricht" required>
                            <Textarea
                                placeholder="Bitte geben Sie eine kurze Nachricht ein..."
                                value={message}
                                onChange={(_, data) => setMessage(data.value)}
                                rows={4}
                            />
                        </Field>

                        <Field label="Dateien" style={{ marginTop: tokens.spacingVerticalL }}>
                            <div {...getRootProps()} className={styles.dropzone}>
                                <input {...getInputProps()} />
                                <Document24Regular />
                                <p>
                                    {isDragActive
                                        ? 'Dateien hier ablegen...'
                                        : 'Dateien hierher ziehen oder klicken zum Auswählen'}
                                </p>
                            </div>

                            {(uploadedFiles.length > 0 || uploadingFiles.length > 0) && (
                                <div className={styles.fileList}>
                                    {uploadedFiles.map((file) => (
                                        <div key={file.hash} className={styles.fileItem}>
                                            <CheckmarkCircle16Regular className={styles.fileIcon} />
                                            <span className={styles.fileName}>{file.name}</span>
                                            <Dismiss24Regular
                                                className={styles.removeButton}
                                                onClick={() => handleRemoveFile(file.hash)}
                                            />
                                        </div>
                                    ))}

                                    {uploadingFiles.map((file) => (
                                        <div key={file.hash} className={styles.fileItem}>
                                            <span className={styles.fileName}>{file.file.name}</span>
                                            {file.status === 'uploading' ? (
                                                <>
                                                    <span>{file.uploadProgress}%</span>
                                                    <Dismiss24Regular
                                                        className={styles.removeButton}
                                                        onClick={() => handleCancelUpload(file.hash)}
                                                    />
                                                </>
                                            ) : (
                                                <span style={{ color: tokens.colorPaletteRedForeground1 }}>
                                                    {file.error || 'Fehler'}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Field>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">Abbrechen</Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting || uploadingFiles.length > 0 || !message.trim()}
                        >
                            {isSubmitting ? <Spinner size="tiny" /> : 'Nachreichen'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
